import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Check, Flame, Clock, Sparkles, Shield, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useCustomAuth } from '@/contexts/CustomAuthContext';
import { subscriptionService } from '@/services/subscriptionService';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import useUserPlan from '@/hooks/useUserPlan';

interface OfertaModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  purchasedPlanId?: 'light' | 'plus' | 'premium';
  /**
   * Fração de desconto exibida (0.20 = 20%). O DESCONTO REAL é aplicado pelo
   * Stripe através do `coupon` — este número é só a apresentação. Manter os
   * dois em sintonia: quem passa `discount` tem que passar o `coupon` que
   * produz esse mesmo desconto, senão a tela promete um valor e a cobrança vem
   * outro.
   */
  discount?: number;
  /** Cupom enviado ao Stripe na ativação. Default: o da primeira oferta. */
  coupon?: string;
  /**
   * Por quanto tempo o desconto vale ("nos 2 primeiros meses", "no primeiro
   * mês"...). Precisa vir da `duration`/`duration_in_months` do cupom no
   * Stripe: um cupom `once` anunciado como "2 meses" promete o dobro do que
   * será cobrado.
   */
  discountDurationLabel?: string;
  /** Selo do topo. Default: o texto da primeira oferta. */
  badgeText?: string;
  /** Parágrafo do header. */
  description?: string;
  /** Texto acessível do diálogo. */
  srTitle?: string;
  /** Faixa de segurança acima dos planos. */
  safetyText?: string;
  /** Nota do rodapé. Passe null para omitir. */
  footerNote?: string | null;
  /**
   * Countdown de 15 min. Faz sentido numa oferta única de boas-vindas; num
   * paywall que reabre a cada tentativa, não — e ao zerar ele DESABILITA os
   * CTAs, o que num paywall significa impedir o usuário de pagar.
   */
  showCountdown?: boolean;
}

const DEFAULT_DISCOUNT = 0.20;
const COUNTDOWN_SECONDS = 15 * 60;

interface Plano {
  id: 'light' | 'plus' | 'premium';
  name: string;
  subtitle: string;
  priceOriginal: number;
  highlight?: boolean;
  badge?: string;
  /** Complemento do microcopy no mobile, específico de cada plano. */
  ctaMicrocopyMobile: string;
  bullets: string[];
  url?: string;
}

const PLANOS: Plano[] = [
  {
    id: 'light',
    name: 'Light',
    get subtitle() { return translateInterfaceText("Para começar com mais limite"); },
    priceOriginal: 97,
    ctaMicrocopyMobile: 'Plano Light nos 2 primeiros meses',
    get bullets() { return [
      translateInterfaceText("6.000 plays por mês"),
      translateInterfaceText("Player premium desbloqueado"),
      translateInterfaceText("Play extra por R$ 0,02"),
      translateInterfaceText("Todos os recursos da HostVSL"),
    ]; },
  },
  {
    id: 'plus',
    name: 'Plus',
    get subtitle() { return translateInterfaceText("Melhor escolha para campanhas ativas"); },
    priceOriginal: 287,
    ctaMicrocopyMobile: 'Plano Plus recomendado',
    get bullets() { return [
      translateInterfaceText("30.000 plays por mês"),
      translateInterfaceText("Ideal para campanhas em escala"),
      translateInterfaceText("Play extra por R$ 0,02"),
      translateInterfaceText("Todos os recursos premium"),
    ]; },
  },
  {
    id: 'premium',
    name: 'Premium',
    get subtitle() { return translateInterfaceText("Para operações com alto volume"); },
    priceOriginal: 587,
    ctaMicrocopyMobile: 'Plano Premium com desconto ativo',
    get bullets() { return [
      translateInterfaceText("60.000 plays por mês"),
      translateInterfaceText("Para alto volume de tráfego"),
      translateInterfaceText("Play extra por R$ 0,02"),
      translateInterfaceText("Todos os recursos premium"),
    ]; },
  },
];

const formatBRL = (n: number) =>
  n.toLocaleString(getInterfaceLanguage(), { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const pad = (n: number) => n.toString().padStart(2, '0');

export const OfertaModal: React.FC<OfertaModalProps> = ({
  open,
  onOpenChange,
  purchasedPlanId,
  discount = DEFAULT_DISCOUNT,
  coupon,
  discountDurationLabel = 'nos 2 primeiros meses',
  badgeText = '20% OFF nos 2 primeiros meses',
  description = translateInterfaceText("Faça o upgrade agora e continue usando a HostVSL com player premium, recursos de conversão e mais limite de plays."),
  srTitle = 'Oferta exclusiva de boas-vindas',
  safetyText = translateInterfaceText("Seus vídeos, configurações e métricas continuam salvos. O upgrade apenas libera o uso completo da plataforma."),
  footerNote = translateInterfaceText("Desconto de 20% válido pelos 2 primeiros meses. Após esse período, o valor normal do plano será cobrado."),
  showCountdown = true,
}) => {
  useLanguage();
  const { user, refreshUser } = useCustomAuth();
  const queryClient = useQueryClient();
  const { isTrial, isLoading: loadingPlan, isFetching, isError } = useUserPlan();
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);

  // Percentual inteiro para os textos ("20% OFF"). Sem desconto, os CTAs não
  // mencionam porcentagem nenhuma.
  const discountPct = Math.round(discount * 100);
  const temDesconto = discount > 0;
  const [processingPlanId, setProcessingPlanId] = useState<'light' | 'plus' | 'premium' | null>(null);

  const handleSubscribe = async (planId: 'light' | 'plus' | 'premium') => {
    if (processingPlanId || !isTrial || isFetching || isError) return;
    if (!user?.id_Stripe_Client) {
      toast.error(translateInterfaceText("Cliente Stripe não encontrado"));
      return;
    }

    const productIdMap: Record<'light' | 'plus' | 'premium', string | undefined> = {
      light: import.meta.env.VITE_PROD_LIGHT_VSL_DYNAMIC97,
      plus: import.meta.env.VITE_PROD_PLUS,
      premium: import.meta.env.VITE_PROD_PREMIUM,
    };
    const productId = productIdMap[planId];

    if (!productId) {
      toast.error(translateInterfaceText("Plano indisponível no momento"));
      return;
    }

    // Sem prop, mantém o cupom da primeira oferta. Com prop, respeita o que o
    // chamador mandou — inclusive string vazia, que significa "sem cupom".
    const cupomAplicado =
      coupon !== undefined
        ? coupon
        : (import.meta.env.VITE_COUPON_FIRST_OFFER as string | undefined) || '';

    setProcessingPlanId(planId);
    try {
      const pending = subscriptionService.getPendingCheckout(user.id_Stripe_Client);
      if (pending) toast.info(translateInterfaceText("Consultando a tentativa anterior com os dados já enviados."));
      const res = await subscriptionService.checkout(user.id_Stripe_Client, {
        product_id: productId,
        currency: 'brl',
        action: 'activate_trial',
        ...(cupomAplicado ? { cupom: cupomAplicado } : {}),
      });

      if (res.resumedDifferentSelection) toast.info(translateInterfaceText("A tentativa anterior foi consultada com o plano, cupom e cartão originais."));
      if (res?.success !== true) {
        toast.error(res.message || translateInterfaceText("Erro ao ativar oferta"));
        return;
      }

      await Promise.all([refreshUser(), queryClient.invalidateQueries({ queryKey: ['userPlan', user?.id_user] })]);
      toast.success(translateInterfaceText("Oferta ativada! Plano atualizado com sucesso."));
      onOpenChange(false);
    } catch {
      toast.error(translateInterfaceText("Erro inesperado ao ativar oferta"));
    } finally {
      setProcessingPlanId(null);
    }
  };

  const planos = PLANOS.map((p) => ({
    ...p,
    highlight: purchasedPlanId ? p.id === purchasedPlanId : p.id === 'plus',
    badge: purchasedPlanId
      ? p.id === purchasedPlanId
        ? 'PLANO ESCOLHIDO'
        : undefined
      : p.id === 'plus'
      ? 'MAIS ESCOLHIDO'
      : undefined,
  }));

  useEffect(() => {
    if (!open || !showCountdown) {
      setSecondsLeft(COUNTDOWN_SECONDS);
      return;
    }
    const id = window.setInterval(() => {
      setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => window.clearInterval(id);
  }, [open, showCountdown]);

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  // Sem countdown não há expiração: os CTAs seguem ativos indefinidamente.
  const expired = showCountdown && secondsLeft === 0;

  const orderedMobile: Plano[] = [
    planos.find((p) => p.id === 'plus')!,
    planos.find((p) => p.id === 'light')!,
    planos.find((p) => p.id === 'premium')!,
  ];

  const renderCard = (plano: Plano, index: number) => {
    const precoFinal = plano.priceOriginal * (1 - discount);
    const isHL = !!plano.highlight;
    const ctaLabel = temDesconto
      ? `Ativar ${plano.name} com ${discountPct}% OFF`
      : `Ativar ${plano.name}`;
    const ctaLabelMobile = temDesconto ? `Ativar com ${discountPct}% OFF` : `Ativar ${plano.name}`;
    const ctaMicrocopy = temDesconto
      ? `${discountPct}% OFF ${discountDurationLabel}`
      : translateInterfaceText("Ativação imediata, cancele quando quiser");
    return (
      <motion.div
        key={plano.id}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.06 }}
        className={[
          'relative rounded-2xl p-6 flex flex-col',
          isHL
            ? 'bg-gradient-to-b from-[#F5F8FF] to-white dark:from-[rgba(47,107,255,0.08)] dark:to-[hsl(var(--bg-card))] border-2 border-[#2F6BFF]'
            : 'bg-white dark:bg-[hsl(var(--bg-card))] border border-[#E6E9F2] dark:border-[hsl(var(--border))]',
        ].join(' ')}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[16px] font-semibold text-[#0F172A] dark:text-foreground">
            {plano.name}
          </span>
          {plano.badge && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-[#EEF4FF] dark:bg-[rgba(47,107,255,0.18)] text-[#2F6BFF] text-[9.5px] font-semibold tracking-[0.08em] uppercase whitespace-nowrap">
              {plano.badge}
            </span>
          )}
        </div>
        <div className="text-[12.5px] text-[#6B7280] dark:text-muted-foreground mt-1 min-h-[34px]">
          {plano.subtitle}
        </div>

        <div className={temDesconto ? 'mt-4 min-h-[96px]' : 'mt-4 min-h-[56px]'}>
          {temDesconto && (
            <div className="text-[12px] text-[#9AA3B2] line-through tabular-nums">
              {translateInterfaceText("De R$")}{" "}{formatBRL(plano.priceOriginal)}{translateInterfaceText("/mês")}</div>
          )}
          <div className="flex items-baseline gap-1.5 mt-0.5">
            <span className="text-[12px] text-[#2F6BFF] font-semibold">R$</span>
            <span className="text-[34px] font-bold leading-none tabular-nums text-[#0F172A] dark:text-foreground">
              {formatBRL(precoFinal)}
            </span>
            <span className="text-[12px] text-[#6B7280] dark:text-muted-foreground">{translateInterfaceText("/mês")}</span>
          </div>
          {temDesconto && (
            <div className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-md bg-[#EEF4FF] dark:bg-[rgba(47,107,255,0.16)]">
              <Sparkles className="w-3 h-3 text-[#2F6BFF]" />
              <span className="text-[11px] font-semibold text-[#2F6BFF]">
                {discountPct}{translateInterfaceText("% OFF ativo")}</span>
            </div>
          )}
        </div>

        <ul className="mt-4 space-y-2 flex-1">
          {plano.bullets.map((b, i) => (
            <li
              key={i}
              className="flex items-start gap-2 text-[12.5px] text-[#374151] dark:text-foreground/85 leading-snug"
            >
              <Check className="w-3.5 h-3.5 mt-0.5 text-[#2F6BFF] flex-shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>

        <div className="mt-6">
          <button
            disabled={expired || processingPlanId !== null || isFetching || isError}
            onClick={() => handleSubscribe(plano.id)}
            className={[
              'w-full h-[48px] rounded-[12px] text-[14px] font-semibold transition-all whitespace-nowrap px-3 inline-flex items-center justify-center gap-2',
              expired || processingPlanId !== null
                ? 'cursor-not-allowed'
                : 'text-white hover:brightness-110 hover:-translate-y-[1px]',
              expired ? 'bg-[#E5E7EB] text-[#9CA3AF]' : '',
            ].join(' ')}
            style={
              !expired
                ? {
                    background: isHL
                      ? 'linear-gradient(90deg, #2F6BFF, #4F8BFF)'
                      : '#2F6BFF',
                    boxShadow: isHL ? '0 8px 20px -10px rgba(47,107,255,0.55)' : undefined,
                    opacity: processingPlanId !== null && processingPlanId !== plano.id ? 0.6 : 1,
                  }
                : undefined
            }
          >
            {processingPlanId === plano.id ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {translateInterfaceText("Processando...")}</>
            ) : expired ? (
              translateInterfaceText("Oferta expirada")
            ) : (
              <>
                <span className="hidden md:inline">{ctaLabel}</span>
                <span className="md:hidden">{ctaLabelMobile}</span>
              </>
            )}
          </button>
          {!expired && (
            <p className="mt-2 text-center text-[11.5px] font-medium text-[#2F6BFF] dark:text-[#7FA7FF] leading-tight">
              <span className="hidden md:inline">{ctaMicrocopy}</span>
              <span className="md:hidden">{plano.ctaMicrocopyMobile}</span>
            </p>
          )}
        </div>
      </motion.div>
    );
  };

  if (loadingPlan || !isTrial || isError) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideCloseButton
        className="!w-[1440px] !max-w-[92vw] sm:!max-w-[88vw] md:!max-w-[80vw] p-0 !overflow-hidden border border-[#E6E9F2] dark:border-[hsl(var(--border))] rounded-[20px] bg-transparent shadow-[0_30px_80px_-20px_rgba(15,23,42,0.35)]"
        style={{
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'min(92vh, 920px)',
        }}
      >
        <DialogTitle className="sr-only">{srTitle}</DialogTitle>

        <div
          className="bg-white dark:bg-[hsl(var(--bg-card))] rounded-[20px] overflow-hidden"
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: '1 1 auto',
            minHeight: 0,
          }}
        >
          {/* Header (fixo no topo) */}
          <div
            className="relative px-5 sm:px-6 md:px-8 py-5 md:py-7 text-white overflow-hidden border-b border-white/10"
            style={{
              flexShrink: 0,
              background:
                'linear-gradient(120deg, #0F2A6B 0%, #1E3A8A 40%, #2F6BFF 100%)',
            }}
          >
            <div className="absolute -top-32 -right-20 w-[360px] h-[360px] rounded-full bg-white/15 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -left-16 w-[280px] h-[280px] rounded-full bg-[#4F8BFF]/30 blur-3xl pointer-events-none" />

            <div className="relative flex flex-col md:flex-row items-start md:items-start justify-between gap-4 md:gap-6">
              <div className="flex-1 min-w-0">
                <div className="inline-flex items-center gap-2 px-3 py-1 sm:px-3.5 sm:py-1.5 rounded-full bg-white text-[#1E3A8A] shadow-[0_8px_20px_-10px_rgba(0,0,0,0.35)] mb-3">
                  <Flame className="w-3.5 h-3.5 text-[#2F6BFF]" />
                  <span className="text-[11px] sm:text-[12px] font-bold tracking-tight">
                    {badgeText}
                  </span>
                </div>
                <p className="text-[12.5px] sm:text-[13.5px] md:text-[14.5px] text-white/85 mt-2 max-w-[560px] leading-relaxed">
                  {description}
                </p>
              </div>

              {/* Cronômetro */}
              {showCountdown && (
              <motion.div
                initial={{ scale: 0.92, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.35, ease: 'easeOut' }}
                className="flex-shrink-0 w-full md:w-auto"
              >
                <div className="bg-white/10 backdrop-blur-md border border-white/25 rounded-2xl px-4 py-3 sm:px-5 sm:py-4 md:min-w-[210px] shadow-[inset_0_1px_0_rgba(255,255,255,0.15)]">
                  <div className="flex items-center gap-1.5 text-white/85 text-[10px] font-semibold tracking-[0.18em] uppercase mb-2">
                    <Clock className="w-3.5 h-3.5" />
                    {expired ? translateInterfaceText("Expirado") : translateInterfaceText("Oferta termina em")}
                  </div>
                  <div className="flex items-end gap-2 tabular-nums">
                    <div className="text-[32px] sm:text-[40px] md:text-[44px] font-bold leading-none tracking-tight">
                      {pad(minutes)}
                    </div>
                    <div className="text-[32px] sm:text-[40px] md:text-[44px] font-bold leading-none text-white/60">:</div>
                    <div className="text-[32px] sm:text-[40px] md:text-[44px] font-bold leading-none tracking-tight">
                      {pad(seconds)}
                    </div>
                  </div>
                  <p className="text-[10.5px] text-white/70 mt-2 leading-tight">
                    {translateInterfaceText("Condição liberada apenas nesta janela.")}</p>
                </div>
              </motion.div>
              )}
            </div>
          </div>

          {/* Body (scrollável) */}
          <div
            className="px-5 sm:px-6 md:px-8 pt-8 pb-7 bg-[#FAFBFD] dark:bg-[hsl(var(--bg-card))]"
            style={{
              flex: '1 1 auto',
              minHeight: 0,
              overflowY: 'auto',
              overscrollBehavior: 'contain',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {/* Safety strip */}
            <div className="flex items-start gap-2.5 px-4 py-3 rounded-xl bg-[#EEF4FF] dark:bg-[rgba(47,107,255,0.10)] border border-[#DCE6FF] dark:border-[rgba(47,107,255,0.25)] mb-8">
              <Shield className="w-4 h-4 text-[#2F6BFF] flex-shrink-0 mt-0.5" />
              <p className="text-[12.5px] text-[#1F2937] dark:text-foreground/90 leading-snug">
                {safetyText}
              </p>
            </div>

            {/* Plans grid */}
            <div className="hidden md:grid grid-cols-3 gap-4 lg:gap-5 pt-3">
              {planos.map((plano, i) => renderCard(plano, i))}
            </div>
            <div className="grid md:hidden grid-cols-1 gap-8 pt-3">
              {orderedMobile.map((plano, i) => renderCard(plano, i))}
            </div>

            {footerNote && (
              <p className="mt-6 text-center text-[11.5px] text-[#6B7280] dark:text-muted-foreground leading-relaxed max-w-[640px] mx-auto">
                {footerNote}
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OfertaModal;